import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { glassSurfaceClassName } from './glass.ts'

const cardVariants = cva(
	'text-card-foreground shadow-brand flex flex-col gap-4 rounded-2xl border p-4',
	{
		variants: {
			variant: {
				default: 'bg-card',
				glass: glassSurfaceClassName,
			},
			// `data-preview="hover"` holds the lift on a resting card, for the
			// styleguide's states row.
			interactive: {
				true: 'hover:shadow-brand-lg data-[preview=hover]:shadow-brand-lg hover:border-brand-border data-[preview=hover]:border-brand-border transition hover:-translate-y-1 data-[preview=hover]:-translate-y-1 motion-reduce:hover:translate-y-0',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
)

/**
 * Card family: a surface that promotes the README idiom (`bg-card
 * text-card-foreground rounded-2xl border`) so consumers stop hand-rolling
 * card markup. Tokens only. `variant="glass"` is the translucent, blurred
 * surface of the header pill (`--surface-glass`, `--surface-glass-border`);
 * the default stays opaque. The root carries the padding, so the sub-parts
 * have none of their own. Compose the root with the sub-parts: `CardHeader`
 * (holding `CardTitle` + `CardDescription`), `CardContent`, and `CardFooter`.
 *
 * `rounded-2xl`, the token border, `shadow-brand` and `p-4` are the one resting
 * card; there is no second shape. `interactive` adds the hover lift (up, to
 * `shadow-brand-lg` and the brand border) and is only for a card that is
 * itself a link: `<Card asChild interactive><Link /></Card>`, or a Card around
 * a single Link whose overlay stretches over the whole card. A card that is
 * not clickable never moves on hover.
 */
function Card({
	className,
	variant,
	interactive,
	asChild = false,
	...props
}: React.ComponentProps<'div'> &
	VariantProps<typeof cardVariants> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : 'div'
	return (
		<Comp
			data-slot="card"
			className={cn(cardVariants({ variant, interactive }), className)}
			{...props}
		/>
	)
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="card-header"
			className={cn('flex flex-col gap-1', className)}
			{...props}
		/>
	)
}

/**
 * The card's title. Pass `asChild` with a heading element when the card is a
 * page section, so the title keeps its place in the document outline:
 * `<CardTitle asChild><h2>Customer</h2></CardTitle>`.
 */
function CardTitle({
	className,
	asChild = false,
	...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : 'div'
	return (
		<Comp
			data-slot="card-title"
			className={cn('text-h6 leading-none font-semibold', className)}
			{...props}
		/>
	)
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="card-description"
			className={cn('text-muted-foreground text-body-xs', className)}
			{...props}
		/>
	)
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="card-content"
			className={cn('text-body-xs', className)}
			{...props}
		/>
	)
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="card-footer"
			className={cn('flex items-center gap-2', className)}
			{...props}
		/>
	)
}

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter }
